import { Injectable } from '@angular/core';
import { Airport } from 'src/app/models/airport';
import { PaymentIntentResponse } from 'src/app/models/payment';
import { LocalStorageService } from '../localStorage/localStorage.service';
import { localStorageKeys } from '../localStorage/localStorageVariables';
import { DutyfreeTravelCountry } from './dutyfree-travel-country.service';

export interface DutyfreeAirportSnapshot {
  code: string;
  name?: string;
  city?: string;
  country?: string;
}

export interface DutyfreeShoppingSession {
  airportCode: string;
  airport?: DutyfreeAirportSnapshot;
  storeId: number | string;
  storeName?: string;
  locationId?: string;
  /** Pickup label e.g. "T2 - International - Arrival" — needed when empty cart omits storeMetaInfo. */
  locationName?: string;
  storeImage?: string;
  updatedAt: string;
}

/** Survives PG cancel / cart remount so payment can reopen without re-checkout. */
export interface DutyfreeCheckoutResume {
  storeId: string;
  locationId: string;
  /** Stable signature of bag lines — invalidate resume when bag changes. */
  cartFingerprint: string;
  /** Pickup date/time only. Any other form answers are stripped on read and write. */
  formValues?: {
    pickup_date?: string;
    pickup_time?: string;
  };
  /** @deprecated Never persist; stripped on read for older payloads. */
  formInfo?: any[];
  /** @deprecated Never persist; stripped on read for older payloads. */
  userInfo?: any;
  paymentIntent?: PaymentIntentResponse | null;
  pendingPaymentUrl?: string | null;
  orderId?: number | string | null;
  captionOrderId?: string | null;
  updatedAt: string;
}

export interface DutyfreeStorageState {
  recentAirports?: Airport[];
  travelCountry?: DutyfreeTravelCountry | null;
  session?: DutyfreeShoppingSession | null;
  /** Adults sharing the customs allowance; scales the guidance limits. */
  travellerCount?: number;
  /** First cart visit already showed the swipe-to-remove peek. */
  cartSwipeHintSeen?: boolean;
  checkoutResume?: DutyfreeCheckoutResume | null;
}

const MAX_TRAVELLER_COUNT = 9;
/** Drop PG resume after this age so stale payment sessions are not reopened. */
const MAX_CHECKOUT_RESUME_AGE_MS = 24 * 60 * 60 * 1000;
const CHECKOUT_RESUME_FORM_KEYS = ['pickup_date', 'pickup_time'] as const;

/** Keep only pickup date/time. Identity, contact, and passport answers are dropped. */
function pickupFormValues(
  formValues: unknown
): DutyfreeCheckoutResume['formValues'] | undefined {
  if (!formValues || typeof formValues !== 'object' || Array.isArray(formValues)) {
    return undefined;
  }
  const source = formValues as Record<string, unknown>;
  const pickup: NonNullable<DutyfreeCheckoutResume['formValues']> = {};
  for (const key of CHECKOUT_RESUME_FORM_KEYS) {
    const value = String(source[key] ?? '').trim();
    if (value) {
      pickup[key] = value;
    }
  }
  return pickup.pickup_date || pickup.pickup_time ? pickup : undefined;
}

@Injectable({
  providedIn: 'root',
})
export class DutyfreeStorageService {
  private migrated = false;

  constructor(private localStorageService: LocalStorageService) {}

  getState(): DutyfreeStorageState {
    this.ensureMigrated();
    const raw = this.localStorageService.getItem(localStorageKeys.DUTYFREE);
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw) as DutyfreeStorageState;
    } catch {
      this.clearAll();
      return {};
    }
  }

  private writeState(patch: Partial<DutyfreeStorageState>): DutyfreeStorageState {
    const next: DutyfreeStorageState = {
      ...this.getState(),
      ...patch,
    };
    this.localStorageService.setItem(
      localStorageKeys.DUTYFREE,
      JSON.stringify(next)
    );
    return next;
  }

  getRecentAirports(): Airport[] {
    const list = this.getState().recentAirports;
    return Array.isArray(list) ? list.slice(0, 3) : [];
  }

  setRecentAirports(airports: Airport[]): void {
    this.writeState({
      recentAirports: (airports || []).slice(0, 3),
    });
  }

  rememberRecentAirport(airport: Airport): Airport[] {
    const entry = {
      airportCode: airport.airportCode,
      airportName: airport.airportName,
      city: airport.city,
      country: airport.country,
      aiportCodeCity:
        airport.aiportCodeCity || `${airport.airportCode}-${airport.city || ''}`,
      latitude: airport.latitude,
      longitude: airport.longitude,
      address: airport.address,
    } as Airport;
    const next = [
      entry,
      ...this.getRecentAirports().filter(
        (item) => item.airportCode !== airport.airportCode
      ),
    ].slice(0, 3);
    this.setRecentAirports(next);
    return next;
  }

  getTravelCountry(): DutyfreeTravelCountry | null {
    const country = this.getState().travelCountry;
    if (!country?.countryCode && !country?.countryName) {
      return null;
    }
    return country;
  }

  setTravelCountry(countryCode: string, countryName: string): void {
    this.writeState({
      travelCountry: { countryCode, countryName },
    });
  }

  clearTravelCountry(): void {
    this.writeState({ travelCountry: null });
  }

  getTravellerCount(): number {
    const count = Number(this.getState().travellerCount);
    if (!Number.isFinite(count) || count < 1) {
      return 1;
    }
    return Math.min(MAX_TRAVELLER_COUNT, Math.floor(count));
  }

  setTravellerCount(count: number): void {
    const safe = Math.min(
      MAX_TRAVELLER_COUNT,
      Math.max(1, Math.floor(Number(count) || 1))
    );
    this.writeState({ travellerCount: safe });
  }

  hasSeenCartSwipeHint(): boolean {
    return this.getState().cartSwipeHintSeen === true;
  }

  markCartSwipeHintSeen(): void {
    this.writeState({ cartSwipeHintSeen: true });
  }

  getSession(): DutyfreeShoppingSession | null {
    const session = this.getState().session;
    if (!session?.airportCode || session.storeId == null) {
      return null;
    }
    return session;
  }

  saveSession(session: Omit<DutyfreeShoppingSession, 'updatedAt'>): void {
    this.writeState({
      session: {
        ...session,
        updatedAt: new Date().toISOString(),
      },
    });
  }

  updateSessionLocation(
    storeId: number | string,
    locationId: string | number,
    locationName?: string
  ): void {
    const session = this.getSession();
    if (!session || String(session.storeId) !== String(storeId)) {
      return;
    }

    const { updatedAt: _updatedAt, ...rest } = session;
    this.saveSession({
      ...rest,
      locationId: String(locationId),
      ...(locationName != null && String(locationName).trim()
        ? { locationName: String(locationName).trim() }
        : {}),
    });
  }

  clearSession(): void {
    this.writeState({ session: null });
  }

  getCheckoutResume(): DutyfreeCheckoutResume | null {
    const resume = this.getState().checkoutResume;
    if (!resume?.storeId || !resume?.locationId || !resume?.cartFingerprint) {
      if (resume) {
        this.clearCheckoutResume();
      }
      return null;
    }
    const updatedAtMs = Date.parse(String(resume.updatedAt || ''));
    if (
      !Number.isFinite(updatedAtMs) ||
      Date.now() - updatedAtMs > MAX_CHECKOUT_RESUME_AGE_MS
    ) {
      this.clearCheckoutResume();
      return null;
    }
    const pickup = pickupFormValues(resume.formValues);
    if (this.checkoutResumeNeedsSanitize(resume, pickup)) {
      const {
        formValues: _fv,
        formInfo: _fi,
        userInfo: _ui,
        updatedAt,
        ...safeRest
      } = resume;
      const sanitized: DutyfreeCheckoutResume = {
        ...safeRest,
        ...(pickup ? { formValues: pickup } : {}),
        updatedAt,
      };
      this.writeState({ checkoutResume: sanitized });
      return sanitized;
    }
    return resume;
  }

  private checkoutResumeNeedsSanitize(
    resume: DutyfreeCheckoutResume,
    pickup: DutyfreeCheckoutResume['formValues'] | undefined
  ): boolean {
    if (resume.formInfo != null || resume.userInfo != null) {
      return true;
    }
    const raw = resume.formValues as Record<string, unknown> | undefined;
    if (raw == null) {
      return false;
    }
    const kept = (pickup || {}) as Record<string, unknown>;
    const rawKeys = Object.keys(raw);
    const keptKeys = Object.keys(kept);
    if (rawKeys.length !== keptKeys.length) {
      return true;
    }
    return keptKeys.some((key) => raw[key] !== kept[key]);
  }

  saveCheckoutResume(
    resume: Omit<DutyfreeCheckoutResume, 'updatedAt'>
  ): DutyfreeCheckoutResume {
    const {
      formValues,
      formInfo: _fi,
      userInfo: _ui,
      ...safeResume
    } = resume;
    const pickup = pickupFormValues(formValues);
    const next: DutyfreeCheckoutResume = {
      ...safeResume,
      ...(pickup ? { formValues: pickup } : {}),
      updatedAt: new Date().toISOString(),
    };
    this.writeState({ checkoutResume: next });
    return next;
  }

  patchCheckoutResume(
    patch: Partial<Omit<DutyfreeCheckoutResume, 'updatedAt'>>
  ): DutyfreeCheckoutResume | null {
    const current = this.getCheckoutResume();
    if (!current) {
      return null;
    }
    const { updatedAt: _updatedAt, ...rest } = current;
    return this.saveCheckoutResume({
      ...rest,
      ...patch,
    });
  }

  clearCheckoutResume(): void {
    this.writeState({ checkoutResume: null });
  }

  clearAll(): void {
    this.localStorageService.removeItem(localStorageKeys.DUTYFREE);
    this.localStorageService.removeItem(localStorageKeys.DUTYFREE_TRAVEL_COUNTRY);
    this.localStorageService.removeItem(localStorageKeys.DUTYFREE_RECENT_AIRPORTS);
  }

  private ensureMigrated(): void {
    if (this.migrated) {
      return;
    }
    this.migrated = true;

    const existing = this.localStorageService.getItem(localStorageKeys.DUTYFREE);
    if (existing) {
      this.localStorageService.removeItem(localStorageKeys.DUTYFREE_TRAVEL_COUNTRY);
      this.localStorageService.removeItem(localStorageKeys.DUTYFREE_RECENT_AIRPORTS);
      return;
    }

    const state: DutyfreeStorageState = {};
    let migrated = false;

    const travelRaw = this.localStorageService.getItem(
      localStorageKeys.DUTYFREE_TRAVEL_COUNTRY
    );
    if (travelRaw) {
      try {
        state.travelCountry = JSON.parse(travelRaw);
        migrated = true;
      } catch {
      }
    }

    const recentRaw = this.localStorageService.getItem(
      localStorageKeys.DUTYFREE_RECENT_AIRPORTS
    );
    if (recentRaw) {
      try {
        const parsed = JSON.parse(recentRaw);
        if (Array.isArray(parsed)) {
          state.recentAirports = parsed.slice(0, 3);
          migrated = true;
        }
      } catch {
      }
    }

    if (migrated) {
      this.localStorageService.setItem(
        localStorageKeys.DUTYFREE,
        JSON.stringify(state)
      );
      this.localStorageService.removeItem(localStorageKeys.DUTYFREE_TRAVEL_COUNTRY);
      this.localStorageService.removeItem(localStorageKeys.DUTYFREE_RECENT_AIRPORTS);
    }
  }
}
